import { environment } from '../../../environments/environment';
import { version as PACKAGE_VERSION } from '../../../../package.json';

/**
 * Static app identity/version metadata — mirrors Consumer's
 * core/config/AppConfig.ts.
 *
 * `VERSION_NAME` used to be four hardcoded literals (`VERSION_MAJOR` /
 * `_MINOR` / `_PATCH` / `_REVISION`) assembled into a string, bumped by hand
 * here on every release. That was a second, independent copy of the app
 * version living alongside `package.json`'s own "version" field — which
 * `scripts/inject-build-metadata.mjs` already reads to stamp the
 * update-banner's displayed version (see AppUpdateService/
 * UpdatePromptComponent). The two drifted apart (package.json sat at
 * "0.0.0", never bumped, while this stayed in sync) — same app, two
 * unrelated numbers shown in different places with no relationship to
 * each other.
 *
 * Now there's exactly one place to bump the version: package.json. Every
 * consumer of `VERSION_NAME` reads it from there:
 *  - This file (`DISPLAY_VERSION`, Profile sidebar footer, login/splash
 *    screens).
 *  - The login API request payload (`login-request-builder.ts`) and
 *    analytics `platform_version` (`device-context.ts`) — this value is
 *    sent to the backend, not just displayed, which is why the format
 *    itself (four dot-separated segments, not standard 3-part semver) was
 *    deliberately preserved rather than changed — only *where* it's
 *    defined changed, not what gets sent.
 *  - `inject-build-metadata.mjs` (the update-banner's version), via the
 *    same package.json field, so it can no longer disagree with this one.
 *
 * This also anticipates a planned future change: CMS-driven, per-version
 * app config, where the PWA will need to request the config that matches
 * its own currently-installed version (and, after the user clicks "Update
 * Now", the newly-activated version's config). That only works cleanly if
 * there is a single, unambiguous version string to key off of — see the
 * forward-looking note in `app-config-repository.service.ts`.
 */
export const AppConfig = {
  JIO_CLOUD_PC_APP_NAME: 'JioPC',
  JIO_CLOUD_APP_ID: 'com.jio.jiopc.smb',
  VERSION_NAME: PACKAGE_VERSION,
  /**
   * Shown in UI footers (Profile sidebar). Production has no suffix;
   * dev/staging append the env label — mirrors Consumer's `AppConfig.ts`
   * `DISPLAY_VERSION` getter, which SMB was previously missing entirely
   * (only `VERSION_NAME` existed), so non-prod builds silently showed a
   * plain version string instead of the env-suffixed one Consumer shows.
   */
  get DISPLAY_VERSION(): string {
    if (environment.production) return this.VERSION_NAME;
    return `${this.VERSION_NAME}-${environment.buildType}`;
  },
} as const;
